/**
 * The one correct way to turn a backend timestamp into text for an operator.
 *
 * WHY THIS MODULE EXISTS. The backend serializes DATETIME2 columns WITHOUT a
 * timezone designator ("2026-08-11T00:40:00") and those values are UTC by
 * contract. Two independent mistakes turn that into a wrong label, and both
 * have to be avoided together:
 *
 *   1. `new Date("2026-08-11T00:40:00")` parses a designator-less string as
 *      BROWSER-LOCAL time, not UTC. On a Bangkok machine that silently keeps
 *      the digits and moves the instant 7 hours.
 *   2. Formatting without an explicit `timeZone` renders in whatever zone the
 *      operator's machine is set to, so the literal "ICT" suffix becomes a
 *      claim the code never checked.
 *
 * P144-F fixed both on the Footstep History page. The War Room board and its
 * Footstep dialog each carried their own copy of the helper that did NEITHER,
 * under a doc comment claiming it was "a real ICT stamp, never a browser-local
 * time relabelled ICT" - so a seeded 07:40 point printed as "00:40 ICT" in the
 * list while RouteMap's popup, which had the fix, printed "07:40 ICT" for the
 * SAME point. Sharing one implementation is what stops the next copy drifting.
 */

export const ICT_TIME_ZONE = "Asia/Bangkok";

/** Backend DATETIME2 values arrive without a timezone designator and are UTC by contract. */
export function parseServerDate(value) {
  if (!value) {
    return null;
  }
  const normalized =
    typeof value === "string" && !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "11 ส.ค. 2026 · 07:40 ICT" - always really ICT, whatever the operator's machine is set to. */
export function formatServerDateTimeIct(value, { fallback = null } = {}) {
  const date = parseServerDate(value);
  if (!date) {
    return fallback;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: ICT_TIME_ZONE,
  });
  const timePart = date.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: ICT_TIME_ZONE,
  });
  return `${datePart} · ${timePart} ICT`;
}
